/**
 * AdminCockpitClassPage — Derwin's editor for the weekly Super Cockpit
 * Class. Lists drafts + published lessons, lets you create/edit/publish
 * with scheduled `week_of` date, offers an AI draft button (Gemini 2.5
 * Flash via Emergent LLM Key), a blast "send now" for the latest
 * published lesson, and a roadmap feedback board aggregated per lesson.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  GraduationCap, Plus, Wand2, Save, Send, Trash2, Loader2, ThumbsUp, ThumbsDown, Sparkles,
} from 'lucide-react';
import { DepartmentNav } from '../components/DepartmentNav';
import { API_URL } from '../config/api';
import { toast } from 'sonner';

const BLANK = {
  title: '', dek: '', metric_tag: 'MAD', hero_emoji: '🌱',
  teaser_md: '', body_md: '', action_prompt: '',
  status: 'draft', week_of: '',
};

const METRICS = ['MAD', 'Dewpoint', 'Delta-T', 'Smith-Kerns', 'GDD', 'SoilTemp'];

export const AdminCockpitClassPage = () => {
  const [lessons, setLessons] = useState([]);
  const [feedback, setFeedback] = useState({ rows: [], by_lesson: [] });
  const [headliner, setHeadliner] = useState(null);
  const [active, setActive] = useState(null); // lesson being edited
  const [form, setForm] = useState(BLANK);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);
  const [aiTopic, setAiTopic] = useState('');
  const [aiAngle, setAiAngle] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [lr, fr, hr] = await Promise.all([
        axios.get(`${API_URL}/api/admin/cockpit-class/lessons`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/cockpit-class/feedback`, { withCredentials: true }),
        axios.get(`${API_URL}/api/widget/headliner`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setLessons(lr.data?.lessons || []);
      setFeedback(fr.data || { rows: [], by_lesson: [] });
      setHeadliner(hr.data?.headline || null);
    } catch (e) {
      toast.error('Failed to load lessons');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startNew = () => {
    setActive(null);
    setForm({ ...BLANK, week_of: new Date().toISOString().slice(0, 10) });
  };

  const startEdit = (lesson) => {
    setActive(lesson);
    setForm({
      title: lesson.title || '', dek: lesson.dek || '',
      metric_tag: lesson.metric_tag || 'MAD', hero_emoji: lesson.hero_emoji || '🌱',
      teaser_md: lesson.teaser_md || '', body_md: lesson.body_md || '',
      action_prompt: lesson.action_prompt || '',
      status: lesson.status || 'draft',
      week_of: lesson.week_of || '',
    });
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error('Title is required');
    setSaving(true);
    try {
      if (active) {
        const r = await axios.patch(`${API_URL}/api/admin/cockpit-class/lessons/${active.id}`, form, { withCredentials: true });
        toast.success(`Updated — ${r.data?.title}`);
      } else {
        const r = await axios.post(`${API_URL}/api/admin/cockpit-class/lessons`, form, { withCredentials: true });
        toast.success(`Created — /cockpit/class/${r.data?.slug}`);
        setActive(r.data);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (lesson) => {
    if (!window.confirm(`Delete "${lesson.title}"?`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/cockpit-class/lessons/${lesson.id}`, { withCredentials: true });
      toast.success('Deleted');
      if (active?.id === lesson.id) { setActive(null); setForm(BLANK); }
      await load();
    } catch {
      toast.error('Delete failed');
    }
  };

  const aiDraft = async () => {
    if (!aiTopic.trim()) return toast.error('Give the AI a topic first');
    setDrafting(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/admin/cockpit-class/lessons/ai-draft`,
        { topic: aiTopic, angle: aiAngle, word_count: 500 },
        { withCredentials: true },
      );
      const d = r.data?.draft || {};
      setForm((f) => ({
        ...f,
        title: d.title || f.title,
        dek: d.dek || f.dek,
        metric_tag: d.metric_tag || f.metric_tag,
        hero_emoji: d.hero_emoji || f.hero_emoji,
        teaser_md: d.teaser_md || f.teaser_md,
        body_md: d.body_md || f.body_md,
        action_prompt: d.action_prompt || f.action_prompt,
      }));
      toast.success('AI draft loaded into the editor');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI draft failed');
    } finally {
      setDrafting(false);
    }
  };

  const sendBlast = async () => {
    if (!window.confirm('Send the newest published lesson to ALL paid users right now?')) return;
    setSending(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/cockpit-class/send-now`, {}, { withCredentials: true });
      toast.success(`Sent ${r.data?.sent || 0} · failed ${r.data?.failed || 0}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Blast failed');
    } finally {
      setSending(false);
    }
  };

  const sendTest = async () => {
    const to = window.prompt('Send test lesson to which email?', 'jus1derwin@gmail.com');
    if (!to) return;
    try {
      const r = await axios.post(`${API_URL}/api/admin/cockpit-class/send-now`, { to }, { withCredentials: true });
      if (r.data?.status === 'sent') toast.success(`Sent · resend_id ${r.data?.resend_id?.slice(0, 8) || ''}`);
      else toast.error(r.data?.error || 'Send failed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    }
  };

  const seedFromHeadliner = () => {
    if (!headliner) return;
    // Pre-fill editor with the weekly signal as the lesson skeleton.
    // Keeps your voice — just removes the "what's the topic?" decision.
    const kindToTag = {
      mad_breach: 'MAD', dollar_spot: 'Smith-Kerns', disease_pressure: 'Smith-Kerns',
      dewpoint_lock: 'Dewpoint', heat_dome: 'SoilTemp', rain_skip: 'MAD',
      pre_timing: 'SoilTemp', greenup_window: 'SoilTemp', pgr_reapply: 'GDD',
      sensor_setup: 'SoilTemp', disease_watch: 'Smith-Kerns',
    };
    setActive(null);
    setForm({
      ...BLANK,
      title: headliner.headline,
      dek: headliner.subhead.slice(0, 140),
      metric_tag: kindToTag[headliner.kind] || 'MAD',
      hero_emoji: headliner.icon || '🌱',
      teaser_md: headliner.subhead,
      body_md: `### What's happening\n\n${headliner.subhead}\n\n### Why a superintendent cares\n\n_(your voice here — why does this matter this week?)_\n\n### How TurfPulse sees it\n\n${headliner.metric}\n\n> Source: ${headliner.source}\n\n### The one call this week\n\n${headliner.action}\n\n### The trap\n\n_(what's the misread most homeowners make here?)_`,
      action_prompt: `What's still confusing about ${kindToTag[headliner.kind] || 'this metric'}? Or what do you want us to build next? Drop it below.`,
      week_of: new Date().toISOString().slice(0, 10),
      status: 'draft',
    });
    toast.success('Signal of the Week loaded — your voice goes in the "Why" + "Trap" sections.');
  };

  return (
    <div data-testid="admin-cockpit-class-page" className="min-h-screen bg-background">
      <DepartmentNav />
      <main className="max-w-6xl mx-auto p-6 space-y-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-emerald-600 font-semibold">
              <GraduationCap className="w-4 h-4" /> Super Cockpit Class · Admin
            </div>
            <h1 className="text-2xl font-bold mt-1">Weekly lesson editor</h1>
            <p className="text-sm text-muted-foreground">One published lesson at a time. Mondays 7 AM local auto-send to paid users.</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={startNew} variant="outline" data-testid="admin-cockpit-new-btn">
              <Plus className="w-4 h-4 mr-1" /> New lesson
            </Button>
            <Button onClick={sendTest} variant="outline" data-testid="admin-cockpit-test-btn">
              Send test
            </Button>
            <Button onClick={sendBlast} disabled={sending} className="bg-amber-600 hover:bg-amber-700" data-testid="admin-cockpit-blast-btn">
              {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />} Blast now
            </Button>
          </div>
        </div>

        {/* ─── Signal of the Week · headliner pre-fill ─── */}
        {headliner && (
          <div
            data-testid="admin-cockpit-signal-card"
            className="rounded-xl border border-emerald-500/30 bg-gradient-to-br from-emerald-50/60 via-background to-background dark:from-emerald-950/30 p-4 sm:p-5"
          >
            <div className="flex items-start gap-4 flex-wrap">
              <div className="text-3xl leading-none">{headliner.icon}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-emerald-700 dark:text-emerald-300 font-bold mb-1">
                  <Sparkles className="w-3.5 h-3.5" /> Signal of the week · rules engine
                  <span className="text-zinc-500 font-normal normal-case">
                    · confidence {Math.round((headliner.confidence || 0) * 100)}%
                  </span>
                </div>
                <div className="font-bold text-foreground text-lg">{headliner.headline}</div>
                <div className="text-sm text-muted-foreground mt-0.5 line-clamp-2">{headliner.subhead}</div>
                <div className="mt-1.5 inline-block text-[11px] font-mono tabular-nums bg-muted/40 px-2 py-0.5 rounded">
                  {headliner.metric}
                </div>
                <div className="mt-2 text-[11px] text-muted-foreground">
                  <span className="font-semibold">→ </span>{headliner.action}
                </div>
              </div>
              <Button
                onClick={seedFromHeadliner}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="admin-cockpit-seed-btn"
              >
                <Sparkles className="w-4 h-4 mr-1" /> Use as this week's lesson
              </Button>
            </div>
            <div className="mt-3 text-[10px] text-muted-foreground">
              Autopilot for topic — manual for voice. Loads a 5-beat scaffold with the math pre-pulled; you write the "Why" and "Trap" sections in your voice.
            </div>
          </div>
        )}

        <Tabs defaultValue="editor">
          <TabsList>
            <TabsTrigger value="editor" data-testid="admin-cockpit-tab-editor">Editor</TabsTrigger>
            <TabsTrigger value="feedback" data-testid="admin-cockpit-tab-feedback">Roadmap feedback</TabsTrigger>
          </TabsList>

          <TabsContent value="editor">
            <div className="grid md:grid-cols-3 gap-5 mt-4">
              {/* Lessons list */}
              <Card className="md:col-span-1">
                <CardHeader>
                  <CardTitle className="text-base">Lessons</CardTitle>
                  <CardDescription>{lessons.length} total · newest first</CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
                  {lessons.map((l) => (
                    <div
                      key={l.id}
                      className={`p-3 rounded-lg border cursor-pointer transition ${active?.id === l.id ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/20' : 'border-border/60 hover:border-border'}`}
                      onClick={() => startEdit(l)}
                      data-testid={`admin-cockpit-lesson-${l.slug}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-start gap-2 flex-1 min-w-0">
                          <span className="text-xl leading-none">{l.hero_emoji}</span>
                          <div className="min-w-0">
                            <div className="text-sm font-medium truncate">{l.title}</div>
                            <div className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5">
                              <Badge variant={l.status === 'published' ? 'default' : 'outline'} className={`text-[9px] ${l.status === 'published' ? 'bg-emerald-600' : ''}`}>
                                {l.status}
                              </Badge>
                              {l.metric_tag && <span>· {l.metric_tag}</span>}
                              {l.week_of && <span>· {l.week_of}</span>}
                            </div>
                          </div>
                        </div>
                        <button onClick={(e) => { e.stopPropagation(); remove(l); }} className="text-muted-foreground hover:text-red-500 transition" data-testid={`admin-cockpit-delete-${l.slug}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Editor */}
              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle className="text-base">{active ? `Edit: ${active.title}` : 'New lesson'}</CardTitle>
                  <CardDescription>
                    Published lessons go live at `/cockpit/class/:slug` and queue up for Monday email.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* AI drafter */}
                  <div className="p-3 rounded-lg border border-dashed border-emerald-500/40 bg-emerald-50/40 dark:bg-emerald-950/20 space-y-2">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-emerald-700 dark:text-emerald-300 font-semibold">
                      <Wand2 className="w-3.5 h-3.5" /> AI draft · Gemini 2.5 Flash
                    </div>
                    <div className="grid sm:grid-cols-2 gap-2">
                      <Input placeholder="Topic (e.g., Dewpoint vs RH)" value={aiTopic} onChange={(e) => setAiTopic(e.target.value)} data-testid="admin-cockpit-ai-topic" />
                      <Input placeholder="Angle / extra context (optional)" value={aiAngle} onChange={(e) => setAiAngle(e.target.value)} data-testid="admin-cockpit-ai-angle" />
                    </div>
                    <Button size="sm" onClick={aiDraft} disabled={drafting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="admin-cockpit-ai-btn">
                      {drafting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Wand2 className="w-4 h-4 mr-1" />} Draft with AI
                    </Button>
                  </div>

                  <div className="grid sm:grid-cols-[auto_1fr] gap-3 items-start">
                    <div>
                      <Label>Emoji</Label>
                      <Input value={form.hero_emoji} onChange={(e) => setForm({ ...form, hero_emoji: e.target.value })} className="w-24 text-center" data-testid="admin-cockpit-emoji" />
                    </div>
                    <div>
                      <Label>Title</Label>
                      <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="admin-cockpit-title" />
                    </div>
                  </div>

                  <div>
                    <Label>Dek (subhead)</Label>
                    <Input value={form.dek} onChange={(e) => setForm({ ...form, dek: e.target.value })} data-testid="admin-cockpit-dek" />
                  </div>

                  <div className="grid sm:grid-cols-3 gap-3">
                    <div>
                      <Label>Metric</Label>
                      <Select value={form.metric_tag} onValueChange={(v) => setForm({ ...form, metric_tag: v })}>
                        <SelectTrigger data-testid="admin-cockpit-metric"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {METRICS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Status</Label>
                      <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                        <SelectTrigger data-testid="admin-cockpit-status"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="draft">Draft</SelectItem>
                          <SelectItem value="published">Published</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Week of</Label>
                      <Input type="date" value={form.week_of} onChange={(e) => setForm({ ...form, week_of: e.target.value })} data-testid="admin-cockpit-week" />
                    </div>
                  </div>

                  <div>
                    <Label>Teaser (free tier + preview cards)</Label>
                    <Textarea rows={3} value={form.teaser_md} onChange={(e) => setForm({ ...form, teaser_md: e.target.value })} data-testid="admin-cockpit-teaser" />
                  </div>

                  <div>
                    <Label>Body (markdown — ###/##/**, -)</Label>
                    <Textarea rows={14} value={form.body_md} onChange={(e) => setForm({ ...form, body_md: e.target.value })} data-testid="admin-cockpit-body" />
                  </div>

                  <div>
                    <Label>"What to build next" prompt</Label>
                    <Input value={form.action_prompt} onChange={(e) => setForm({ ...form, action_prompt: e.target.value })} data-testid="admin-cockpit-action" />
                  </div>

                  <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="admin-cockpit-save">
                    {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                    {active ? 'Save changes' : 'Create lesson'}
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="feedback">
            <div className="space-y-3 mt-4">
              {feedback.by_lesson?.length === 0 && (
                <Card><CardContent className="py-10 text-center text-muted-foreground">No feedback yet.</CardContent></Card>
              )}
              {feedback.by_lesson?.map((l) => (
                <Card key={l.lesson_slug} data-testid={`admin-cockpit-feedback-${l.lesson_slug}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <CardTitle className="text-base">{l.lesson_title || l.lesson_slug}</CardTitle>
                      <div className="flex gap-3 text-sm">
                        <span className="text-emerald-600 flex items-center gap-1"><ThumbsUp className="w-3.5 h-3.5" /> {l.up}</span>
                        <span className="text-red-500 flex items-center gap-1"><ThumbsDown className="w-3.5 h-3.5" /> {l.down}</span>
                        <span className="text-muted-foreground">Use {l.use} · Not yet {l.not_yet} · Already {l.already}</span>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {l.texts?.length === 0 && <p className="text-xs text-muted-foreground">No written feedback yet.</p>}
                    {l.texts?.map((t, j) => (
                      <div key={j} className="p-3 rounded-lg border border-border/60 bg-muted/20">
                        <div className="text-[11px] text-muted-foreground">
                          {t.email} · {t.created_at ? new Date(t.created_at).toLocaleString() : ''}
                        </div>
                        <div className="text-sm mt-1">{t.text}</div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminCockpitClassPage;
